/**
 * BOSS İzin Onay/Red Modal'ı — her durumdan durum değiştirilebilir.
 *
 * Bekliyor:    Onayla / Reddet
 * Onaylı:      Redde Çevir / İzni İptal Et
 * Reddedildi:  Onaya Çevir
 * İptal:       Onaya Çevir / Redde Çevir
 */

import { useEffect, useState } from 'react'
import { Check, X, MessageSquare, Wallet, AlertTriangle } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { useAuth } from '@/contexts/AuthContext'
import {
  izinOnayla, izinReddet, izinIptal,
  KAYNAK_ETIKETLERI, DURUM_ETIKETLERI,
  type IzinTalebi, type IzinKaynagi, type IzinDagilimi, type IzinDurumu,
} from '@/lib/services/izin-service'
import { personelIzinOzeti, type IzinOzeti } from '@/lib/services/izin-bakiye'
import type { KullaniciPersonel } from '@/types'

interface Props {
  acik: boolean
  talep: IzinTalebi | null
  personel?: KullaniciPersonel
  onKapat: () => void
  onIslem: () => void
}

function tarihEtiketi(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })
}

function dagilimHesapla(
  gunSayisi: number,
  kaynak: IzinKaynagi,
  ozet: IzinOzeti | null
): IzinDagilimi {
  if (kaynak === 'maas') {
    return { yillik: 0, aylik: 0, maas: gunSayisi }
  }
  if (kaynak === 'yillik') {
    const mevcut = Math.max(0, ozet?.yillik?.kalan ?? 0)
    const kullan = Math.min(gunSayisi, mevcut)
    return { yillik: kullan, aylik: 0, maas: gunSayisi - kullan }
  }
  const mevcut = Math.max(0, ozet?.aylik.kalan ?? 0)
  const kullan = Math.min(gunSayisi, mevcut)
  return { yillik: 0, aylik: kullan, maas: gunSayisi - kullan }
}

function durumRengi(d: IzinDurumu): string {
  switch (d) {
    case 'onaylandi': return 'bg-green-100 text-green-800 border-green-300'
    case 'reddedildi': return 'bg-red-100 text-red-800 border-red-300'
    case 'iptal': return 'bg-gray-100 text-gray-700 border-gray-300'
    default: return 'bg-yellow-100 text-yellow-800 border-yellow-300'
  }
}

export function IzinOnayModal({ acik, talep, personel, onKapat, onIslem }: Props) {
  const { firebaseUser } = useAuth()

  type Mod = 'goruntule' | 'onay' | 'red'
  const [mod, setMod] = useState<Mod>('goruntule')
  const [kaynak, setKaynak] = useState<IzinKaynagi>('aylik')
  const [redSebebi, setRedSebebi] = useState('')
  const [isleniyor, setIsleniyor] = useState(false)
  const [hata, setHata] = useState<string | null>(null)

  const [ozet, setOzet] = useState<IzinOzeti | null>(null)
  const [ozetYukleniyor, setOzetYukleniyor] = useState(false)

  useEffect(() => {
    if (!acik || !talep || !personel) return
    setMod('goruntule')
    setKaynak(talep.kaynak ?? 'aylik')
    setRedSebebi('')
    setHata(null)
    setOzet(null)

    setOzetYukleniyor(true)
    personelIzinOzeti(
      personel.id,
      personel.iseBaslamaTarihi ?? null,
      personel.calismaTipi === 'fulltime',
      personel.yillikIzinBaslangicBakiye ?? 0
    )
      .then(setOzet)
      .catch((err) => {
        console.error('Bakiye yüklenemedi:', err)
        setHata('Bakiye yüklenemedi: ' + (err as Error).message)
      })
      .finally(() => setOzetYukleniyor(false))
  }, [acik, talep?.id, personel?.id])

  if (!talep) return null

  const dagilim = dagilimHesapla(talep.gunSayisi, kaynak, ozet)
  const tamamenKaynaktan = dagilim.maas === 0

  async function onayla() {
    if (!firebaseUser) return
    setIsleniyor(true)
    setHata(null)
    try {
      await izinOnayla(talep!.id, dagilim, kaynak, firebaseUser.uid)
      onIslem()
    } catch (err) {
      setHata((err as Error).message)
    } finally {
      setIsleniyor(false)
    }
  }

  async function reddet() {
    if (!firebaseUser) return
    if (talep!.durum === 'onaylandi') {
      if (!confirm('Bu izin daha önce onaylandı. Reddetmek istediğine emin misin?')) {
        return
      }
    }
    setIsleniyor(true)
    setHata(null)
    try {
      await izinReddet(talep!.id, firebaseUser.uid, redSebebi.trim() || undefined)
      onIslem()
    } catch (err) {
      setHata((err as Error).message)
    } finally {
      setIsleniyor(false)
    }
  }

  async function iptal() {
    if (!firebaseUser) return
    if (!confirm('Bu izni iptal etmek istediğine emin misin?')) return
    setIsleniyor(true)
    setHata(null)
    try {
      await izinIptal(talep!.id, firebaseUser.uid, 'BOSS tarafından iptal edildi')
      onIslem()
    } catch (err) {
      setHata((err as Error).message)
    } finally {
      setIsleniyor(false)
    }
  }

  const durum = talep.durum
  const onayli = durum === 'onaylandi'
  const reddedildi = durum === 'reddedildi'
  const iptalli = durum === 'iptal'

  return (
    <Modal acik={acik} onKapat={onKapat} baslik="İzin Talebi" boyut="md">
      <div className="space-y-4">
        <div className="bg-brand-50 border border-brand-200 rounded-xl p-3">
          <p className="text-xs text-brand-700 uppercase tracking-wide font-semibold mb-0.5">
            Personel
          </p>
          <p className="font-bold text-brand-900 text-lg">
            {personel?.isim ?? 'Bilinmeyen'}
          </p>
          {personel?.iseBaslamaTarihi && (
            <p className="text-xs text-brand-700 mt-0.5">
              İşe başlama: {tarihEtiketi(personel.iseBaslamaTarihi)}
            </p>
          )}
        </div>

        {/* Mevcut durum büyük rozet */}
        <div className={`rounded-xl border-2 p-3 text-center ${durumRengi(durum)}`}>
          <p className="text-[10px] uppercase tracking-wide font-semibold mb-0.5 opacity-75">
            Mevcut Durum
          </p>
          <p className="font-bold text-lg">{DURUM_ETIKETLERI[durum]}</p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="bg-gray-50 rounded-xl p-3 text-center">
            <p className="text-[10px] uppercase text-gray-600 font-semibold">İlk Gün</p>
            <p className="font-bold text-gray-900 text-sm mt-0.5">
              {tarihEtiketi(talep.baslangicTarihi)}
            </p>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 text-center">
            <p className="text-[10px] uppercase text-gray-600 font-semibold">İşbaşı</p>
            <p className="font-bold text-gray-900 text-sm mt-0.5">
              {tarihEtiketi(talep.bitisTarihi)}
            </p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 text-center">
            <p className="text-[10px] uppercase text-purple-600 font-semibold">Toplam</p>
            <p className="font-bold text-purple-900 text-xl mt-0.5">
              {talep.gunSayisi}
              <span className="text-xs font-normal text-purple-700 ml-1">gün</span>
            </p>
          </div>
        </div>

        {talep.aciklama && (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
            <div className="flex items-start gap-2">
              <MessageSquare className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-gray-600 mb-0.5">Açıklama</p>
                <p className="text-sm text-gray-900">{talep.aciklama}</p>
              </div>
            </div>
          </div>
        )}

        {/* Onaylı izinler için kaynak dağılımı göster */}
        {onayli && mod === 'goruntule' && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-3">
            <p className="text-xs uppercase font-bold text-green-800 mb-2">
              Kaynak Dağılımı
            </p>
            <div className="space-y-1 text-sm">
              {(talep.yillikGun ?? 0) > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-700">Yıllık:</span>
                  <span className="font-semibold text-purple-700">{talep.yillikGun} gün</span>
                </div>
              )}
              {(talep.aylikGun ?? 0) > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-700">Aylık:</span>
                  <span className="font-semibold text-blue-700">{talep.aylikGun} gün</span>
                </div>
              )}
              {(talep.maasGun ?? 0) > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-700">Maaştan:</span>
                  <span className="font-semibold text-amber-700">{talep.maasGun} gün</span>
                </div>
              )}
              {talep.yillikGun === undefined && talep.kaynak && (
                <div className="flex justify-between">
                  <span className="text-gray-700">{KAYNAK_ETIKETLERI[talep.kaynak]}:</span>
                  <span className="font-semibold">{talep.gunSayisi} gün</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reddedilen izin için sebep göster */}
        {reddedildi && mod === 'goruntule' && talep.redSebebi && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3">
            <p className="text-xs uppercase font-bold text-red-800 mb-1">Red Sebebi</p>
            <p className="text-sm text-red-900">{talep.redSebebi}</p>
          </div>
        )}

        {/* Onay onay modu kaynak bilgisi */}
        {mod === 'onay' && ozet && (
          <div className="grid grid-cols-2 gap-2 text-center">
            {ozet.yillik && (
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-2">
                <p className="text-[10px] uppercase text-purple-600 font-semibold">Yıllık Kalan</p>
                <p className="font-bold text-purple-900 text-lg">{ozet.yillik.kalan} gün</p>
                <p className="text-[9px] text-purple-700">
                  {ozet.yillik.kazanilan} kaz + {ozet.yillik.baslangicBakiye} başl − {ozet.yillik.kullanilan} kul
                </p>
              </div>
            )}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-2">
              <p className="text-[10px] uppercase text-blue-600 font-semibold">Aylık Kalan</p>
              <p className="font-bold text-blue-900 text-lg">{ozet.aylik.kalan} gün</p>
              <p className="text-[9px] text-blue-700">
                {ozet.aylik.vardiyaSayisi} vardiya / 6 = {ozet.aylik.hak}
              </p>
            </div>
          </div>
        )}

        {/* Görüntüleme modu — aksiyon butonları */}
        {mod === 'goruntule' && (
          <div className="border-t pt-3 space-y-2">
            <p className="text-xs uppercase font-bold text-gray-600 mb-2">
              Durumu Değiştir
            </p>
            <div className="grid grid-cols-2 gap-2">
              {!onayli && (
                <button
                  onClick={() => setMod('onay')}
                  disabled={ozetYukleniyor || isleniyor}
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <Check className="w-4 h-4" />
                  {onayli ? 'Onaylandı' : (reddedildi || iptalli ? 'Onaya Çevir' : 'Onayla')}
                </button>
              )}
              {!reddedildi && (
                <button
                  onClick={() => setMod('red')}
                  disabled={isleniyor}
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <X className="w-4 h-4" />
                  {onayli ? 'Redde Çevir' : (iptalli ? 'Redde Çevir' : 'Reddet')}
                </button>
              )}
            </div>

            {onayli && (
              <button
                onClick={iptal}
                disabled={isleniyor}
                className="w-full bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-800 font-semibold py-2.5 rounded-xl inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <X className="w-4 h-4" />
                İzni İptal Et
              </button>
            )}

            {hata && (
              <p className="text-red-600 text-sm mt-2">{hata}</p>
            )}
          </div>
        )}

        {/* Onay akışı */}
        {mod === 'onay' && (
          <div className="bg-green-50 border border-green-300 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-700" />
              <h3 className="font-bold text-green-900">
                {onayli ? 'Onayı Güncelle' : 'Onaylanıyor'}
              </h3>
            </div>

            {(reddedildi || iptalli) && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-2.5 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-900">
                  Bu talep daha önce <strong>{DURUM_ETIKETLERI[durum]}</strong> idi. Onaylarsan personelin izin hakkı uygulanacak.
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-green-900 mb-2">
                Kaynak Seç
              </label>
              <div className="space-y-2">
                {(['yillik', 'aylik', 'maas'] as IzinKaynagi[]).map((k) => (
                  <label
                    key={k}
                    className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                      kaynak === k
                        ? 'bg-white border-green-500 shadow-sm'
                        : 'bg-white/50 border-green-200 hover:bg-white'
                    }`}
                  >
                    <input
                      type="radio"
                      name="kaynak"
                      value={k}
                      checked={kaynak === k}
                      onChange={() => setKaynak(k)}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-gray-900">
                        {KAYNAK_ETIKETLERI[k]}
                      </p>
                      <p className="text-xs text-gray-600">
                        {k === 'yillik' && 'Yıllık ödenekli izin havuzundan düşülür.'}
                        {k === 'aylik' && 'Aylık formül havuzundan düşülür (vardiya/6).'}
                        {k === 'maas' && `Maaştan ${talep.gunSayisi} günlük yevmiye kesilir.`}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {kaynak !== 'maas' && (
              <div className={`rounded-xl p-3 border-2 ${
                tamamenKaynaktan
                  ? 'bg-white border-green-300'
                  : 'bg-amber-50 border-amber-300'
              }`}>
                <p className="text-xs uppercase font-bold text-gray-700 mb-2">
                  Dağılım Önizleme
                </p>
                <div className="space-y-1.5 text-sm">
                  {dagilim.yillik > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">Yıllık havuzdan:</span>
                      <span className="font-bold text-purple-700">
                        {dagilim.yillik} gün
                      </span>
                    </div>
                  )}
                  {dagilim.aylik > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">Aylık havuzdan:</span>
                      <span className="font-bold text-blue-700">
                        {dagilim.aylik} gün
                      </span>
                    </div>
                  )}
                  {dagilim.maas > 0 && (
                    <div className="flex items-center justify-between border-t border-amber-200 pt-1.5 mt-1.5">
                      <span className="text-amber-800 font-semibold inline-flex items-center gap-1">
                        <Wallet className="w-4 h-4" />
                        Maaştan kesilecek:
                      </span>
                      <span className="font-bold text-amber-900">
                        {dagilim.maas} gün
                      </span>
                    </div>
                  )}
                </div>
                {!tamamenKaynaktan && (
                  <p className="text-[11px] text-amber-700 mt-2">
                    ⚠ Bakiye yetersiz. Kalan {dagilim.maas} gün maaştan kesilecek.
                  </p>
                )}
              </div>
            )}

            {hata && (
              <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg p-2">
                {hata}
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <button
                onClick={() => setMod('goruntule')}
                disabled={isleniyor}
                className="btn-secondary flex-1"
              >
                Geri
              </button>
              <button
                onClick={onayla}
                disabled={isleniyor || ozetYukleniyor}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 rounded-xl flex-1 inline-flex items-center justify-center gap-2"
              >
                <Check className="w-4 h-4" />
                {isleniyor ? 'İşleniyor...' : (onayli ? 'Güncelle' : 'Onayı Tamamla')}
              </button>
            </div>
          </div>
        )}

        {/* Red akışı */}
        {mod === 'red' && (
          <div className="bg-red-50 border border-red-300 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <X className="w-5 h-5 text-red-700" />
              <h3 className="font-bold text-red-900">Reddediliyor</h3>
            </div>

            {onayli && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-2.5 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-900">
                  Bu izin daha önce <strong>onaylandı</strong>. Reddedersen personelin izin hakkı geri alınacak.
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-red-900 mb-1.5">
                Red Sebebi (opsiyonel)
              </label>
              <textarea
                value={redSebebi}
                onChange={(e) => setRedSebebi(e.target.value)}
                rows={3}
                maxLength={300}
                placeholder="Örn: Bu hafta yoğunluk var..."
                className="input-base resize-none"
              />
            </div>
            {hata && (
              <div className="bg-red-100 border border-red-200 text-red-800 text-sm rounded-lg p-2">
                {hata}
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => setMod('goruntule')}
                disabled={isleniyor}
                className="btn-secondary flex-1"
              >
                Geri
              </button>
              <button
                onClick={reddet}
                disabled={isleniyor}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 rounded-xl flex-1 inline-flex items-center justify-center gap-2"
              >
                <X className="w-4 h-4" />
                {isleniyor ? 'İşleniyor...' : 'Reddi Tamamla'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
